import { Card, Badge } from '@/core/ui/components';
import type { TenantDashboardAdmin } from '@yawi/types';
import { Users, User, Clock, Shield } from 'lucide-react';

interface AdminsListCardProps {
  admins: TenantDashboardAdmin[];
}

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  TENANT_ADMIN: 'Admin',
  TENANT_USER: 'Usuario',
  super_admin: 'Super Admin',
  admin: 'Admin',
  viewer: 'Viewer',
};

const ROLE_COLORS: Record<string, 'purple' | 'success' | 'default' | 'warning'> = {
  SUPER_ADMIN: 'purple',
  TENANT_ADMIN: 'success',
  TENANT_USER: 'default',
};

const STATUS_BADGES: Record<string, { variant: 'success' | 'warning' | 'error' | 'default'; label: string }> = {
  ACTIVE: { variant: 'success', label: 'Activo' },
  active: { variant: 'success', label: 'Activo' },
  INVITED: { variant: 'warning', label: 'Invitado' },
  invited: { variant: 'warning', label: 'Invitado' },
  DISABLED: { variant: 'error', label: 'Deshabilitado' },
  disabled: { variant: 'error', label: 'Deshabilitado' },
};

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Nunca';

  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 60000) return 'Hace un momento';
  if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)}m`;
  if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)}h`;
  if (diff < 604800000) return `Hace ${Math.floor(diff / 86400000)}d`;
  return date.toLocaleDateString();
}

export function AdminsListCard({ admins }: AdminsListCardProps) {
  if (!admins || admins.length === 0) {
    return (
      <Card>
        <h3 className="text-lg font-semibold text-theme mb-4 flex items-center gap-2">
          <Users className="w-5 h-5" />
          Administradores
        </h3>
        <p className="text-theme-secondary text-center py-4">
          Sin administradores registrados
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <h3 className="text-lg font-semibold text-theme mb-4 flex items-center gap-2">
        <Users className="w-5 h-5" />
        Usuarios ({admins.length})
      </h3>

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {admins.map((admin) => {
          const statusBadge = STATUS_BADGES[admin.status] || STATUS_BADGES.ACTIVE;
          const roleColor = ROLE_COLORS[admin.role] || 'default';

          return (
            <div
              key={admin.id}
              className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50"
            >
              <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                <User className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-theme truncate">{admin.name}</p>
                  <Badge variant={statusBadge.variant} className="text-xs">
                    {statusBadge.label}
                  </Badge>
                  {admin.twoFactorEnabled && (
                    <span title="2FA habilitado">
                      <Shield className="w-3 h-3 text-green-600" />
                    </span>
                  )}
                </div>
                <p className="text-xs text-theme-secondary truncate">{admin.email}</p>
              </div>

              <div className="text-right shrink-0">
                <Badge variant={roleColor} className="text-xs">
                  {ROLE_LABELS[admin.role] || admin.role}
                </Badge>
                <p className="text-xs text-theme-secondary mt-1 flex items-center gap-1 justify-end">
                  <Clock className="w-3 h-3" />
                  {formatRelativeTime(admin.lastLoginAt)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
